import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "store";
import { dismissSitePreviewExpired } from "store/siteImagesSlice";
import { writePreviewToken } from "./SiteImagesBoot";

const PILL = "fixed bottom-4 left-1/2 z-[9999] flex -translate-x-1/2 items-center gap-3 rounded-full px-4 py-2 text-sm shadow-lg";
const PILL_BUTTON = "rounded-full border border-current px-3 py-0.5 font-medium";

/**
 * Shown only while a backoffice preview link is active in this tab: the screen is showing UNPUBLISHED
 * images, so it must say so. "Exit" drops the token and reloads, which boots the published set again.
 * A refused (expired) link is said here too, until dismissed — mounted above the routes, this is the
 * one place every screen has, the signed-out ones included.
 */
const SitePreviewBanner = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const active = useAppSelector((s) => s.siteImages.previewActive);
  const expired = useAppSelector((s) => s.siteImages.previewExpired);
  const expiresAt = useAppSelector((s) => s.siteImages.previewExpiresAt);

  if (expired) {
    return (
      <div role="alert" data-testid="site-preview-expired" className={`${PILL} bg-destructive text-white`}>
        <span>{t("common:site_images.preview_expired")}</span>
        <button type="button" onClick={() => dispatch(dismissSitePreviewExpired())} className={PILL_BUTTON}>
          {t("common:close")}
        </button>
      </div>
    );
  }
  if (!active) return null;

  const until = expiresAt ? new Date(expiresAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  const exit = () => {
    writePreviewToken(null);
    window.location.reload();
  };

  return (
    <div role="status" data-testid="site-preview-banner" className={`${PILL} bg-primary text-primary-foreground`}>
      <span>{t("common:site_images.preview_banner")}</span>
      {until ? <span className="opacity-70">{t("common:site_images.preview_until", { time: until })}</span> : null}
      <button type="button" onClick={exit} className={PILL_BUTTON}>
        {t("common:site_images.preview_exit")}
      </button>
    </div>
  );
};

export default SitePreviewBanner;
